import React, { useState, useEffect } from 'react';
import Layout from './Layout';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const METRIC_LABELS = {
  cpu: 'CPU Usage (%)',
  ram: 'RAM Usage (%)',
  disk: 'Disk Usage (%)',
  network_in: 'Network In (MB/s)',
  network_out: 'Network Out (MB/s)',
};

function AlertsDashboard() {
  const [activeTab, setActiveTab] = useState('rules');
  const [rules, setRules] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [incidentFilter, setIncidentFilter] = useState('firing');
  const [error, setError] = useState(null);

  const token = localStorage.getItem('access_token');

  const fetchData = async () => {
    try {
      setError(null);
      const [rulesRes, incidentsRes, instancesRes] = await Promise.all([
        fetch(`${API_URL}/v1/alerts/rules`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/v1/alerts/incidents?status=${incidentFilter}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/v1/instances`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      const [rulesData, incidentsData, instancesData] = await Promise.all([
        rulesRes.json(), incidentsRes.json(), instancesRes.json(),
      ]);
      setRules(rulesData.alert_rules || []);
      setIncidents(incidentsData.incidents || []);
      setInstances(instancesData.instances || []);
    } catch (err) {
      setError('Failed to load alerts data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incidentFilter]);

  const createRule = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    try {
      const res = await fetch(`${API_URL}/v1/alerts/rules`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: fd.get('name'),
          resource_type: 'instance',
          resource_id: fd.get('resource_id') || undefined,
          metric: fd.get('metric'),
          condition: fd.get('condition'),
          threshold: parseFloat(fd.get('threshold')),
          duration_minutes: parseInt(fd.get('duration_minutes')) || 5,
          notification_email: fd.get('notification_email') || undefined,
          notification_webhook: fd.get('notification_webhook') || undefined,
        }),
      });
      if (res.ok) {
        setShowCreateModal(false);
        fetchData();
      } else {
        const err = await res.json();
        alert('Error: ' + (err.error || 'Failed to create rule'));
      }
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const toggleRule = async (rule) => {
    await fetch(`${API_URL}/v1/alerts/rules/${rule.id}`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled: !rule.enabled }),
    });
    fetchData();
  };

  const deleteRule = async (ruleId) => {
    if (!window.confirm('Delete this alert rule? All open incidents will be resolved.')) return;
    await fetch(`${API_URL}/v1/alerts/rules/${ruleId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    fetchData();
  };

  const getInstanceName = (id) => {
    if (!id) return 'All instances';
    const inst = instances.find(i => i.id === id);
    return inst ? inst.name : id.substring(0, 8) + '…';
  };

  const firingCount = incidents.filter(i => i.status === 'firing').length;

  if (loading) {
    return (
      <Layout userType="customer">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-400">Loading alerts…</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout userType="customer">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-white">Monitoring &amp; Alerts</h1>
            <p className="text-gray-400 mt-1">
              {firingCount > 0
                ? <span className="text-red-400 font-medium">⚠ {firingCount} incident{firingCount !== 1 ? 's' : ''} firing</span>
                : <span className="text-green-400">✓ All clear</span>}
            </p>
          </div>
          {activeTab === 'rules' && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-lg font-medium"
            >
              + New Alert Rule
            </button>
          )}
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-900/40 border border-red-700 rounded-lg text-red-300">{error}</div>
        )}

        {/* Tabs */}
        <div className="flex space-x-3 mb-6">
          {['rules', 'incidents'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-5 py-2.5 rounded-lg font-medium capitalize ${
                activeTab === tab ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'
              }`}
            >
              {tab === 'incidents' && firingCount > 0
                ? `Incidents (${firingCount})`
                : tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Rules Tab */}
        {activeTab === 'rules' && (
          <div className="bg-gray-800 rounded-lg overflow-hidden">
            {rules.length === 0 ? (
              <div className="text-center py-16 text-gray-400">
                <div className="text-5xl mb-4">🔔</div>
                <div className="text-lg font-medium mb-2">No alert rules yet</div>
                <div className="text-sm">Create a rule to get notified when a metric crosses a threshold.</div>
              </div>
            ) : (
              <table className="min-w-full">
                <thead className="bg-gray-700">
                  <tr>
                    {['Rule', 'Resource', 'Condition', 'Notifications', 'Status', 'Actions'].map(h => (
                      <th key={h} className="px-5 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {rules.map(rule => (
                    <tr key={rule.id} className="hover:bg-gray-750">
                      <td className="px-5 py-4">
                        <div className="text-white font-medium">{rule.name}</div>
                        <div className="text-gray-400 text-xs mt-0.5">{METRIC_LABELS[rule.metric] || rule.metric}</div>
                      </td>
                      <td className="px-5 py-4 text-gray-300 text-sm">{getInstanceName(rule.resource_id)}</td>
                      <td className="px-5 py-4">
                        <span className="font-mono text-sm text-yellow-300">
                          {rule.metric} {rule.condition} {rule.threshold}%
                        </span>
                        <div className="text-gray-400 text-xs mt-0.5">for {rule.duration_minutes} min</div>
                      </td>
                      <td className="px-5 py-4 text-gray-400 text-sm">
                        {rule.notification_email && (
                          <div className="flex items-center gap-1">
                            <span>📧</span>
                            <span className="truncate max-w-32">{rule.notification_email}</span>
                          </div>
                        )}
                        {rule.notification_webhook && (
                          <div className="flex items-center gap-1">
                            <span>🔗</span>
                            <span className="text-xs text-gray-500">webhook</span>
                          </div>
                        )}
                        {!rule.notification_email && !rule.notification_webhook && (
                          <span className="text-gray-600">none</span>
                        )}
                      </td>
                      <td className="px-5 py-4">
                        <button
                          onClick={() => toggleRule(rule)}
                          className={`px-2.5 py-1 rounded text-xs font-medium ${
                            rule.enabled
                              ? 'bg-green-900 text-green-300 hover:bg-green-800'
                              : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                          }`}
                        >
                          {rule.enabled ? 'Active' : 'Disabled'}
                        </button>
                      </td>
                      <td className="px-5 py-4">
                        <button
                          onClick={() => deleteRule(rule.id)}
                          className="text-red-400 hover:text-red-300 text-sm"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Incidents Tab */}
        {activeTab === 'incidents' && (
          <div>
            {/* Filter */}
            <div className="flex gap-3 mb-4">
              {['firing', 'resolved'].map(s => (
                <button
                  key={s}
                  onClick={() => setIncidentFilter(s)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium capitalize ${
                    incidentFilter === s
                      ? s === 'firing' ? 'bg-red-700 text-white' : 'bg-gray-600 text-white'
                      : 'bg-gray-800 text-gray-400 hover:text-white'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>

            <div className="bg-gray-800 rounded-lg overflow-hidden">
              {incidents.length === 0 ? (
                <div className="text-center py-16 text-gray-400">
                  <div className="text-5xl mb-4">{incidentFilter === 'firing' ? '✅' : '📋'}</div>
                  <div className="text-lg font-medium">
                    {incidentFilter === 'firing' ? 'No active incidents' : 'No resolved incidents'}
                  </div>
                </div>
              ) : (
                <table className="min-w-full">
                  <thead className="bg-gray-700">
                    <tr>
                      {['Rule', 'Resource', 'Metric', 'Value vs Threshold', 'Duration', 'Notified'].map(h => (
                        <th key={h} className="px-5 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {incidents.map(inc => {
                      const started = new Date(inc.started_at);
                      const ended = inc.resolved_at ? new Date(inc.resolved_at) : new Date();
                      const mins = Math.round((ended - started) / 60000);
                      const duration = mins < 60 ? `${mins}m` : `${Math.round(mins / 60)}h ${mins % 60}m`;

                      return (
                        <tr key={inc.id} className="hover:bg-gray-750">
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-2">
                              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                inc.status === 'firing' ? 'bg-red-400 animate-pulse' : 'bg-gray-500'
                              }`} />
                              <span className="text-white font-medium">{inc.rule_name}</span>
                            </div>
                          </td>
                          <td className="px-5 py-4 text-gray-300 text-sm font-mono">
                            {inc.resource_id?.substring(0, 8)}…
                          </td>
                          <td className="px-5 py-4 text-gray-300 text-sm">
                            {METRIC_LABELS[inc.metric] || inc.metric}
                          </td>
                          <td className="px-5 py-4">
                            <span className="text-red-300 font-mono font-medium">
                              {inc.actual_value?.toFixed(1)}
                            </span>
                            <span className="text-gray-500 text-sm"> / {inc.threshold}</span>
                          </td>
                          <td className="px-5 py-4 text-gray-400 text-sm">{duration}</td>
                          <td className="px-5 py-4">
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              inc.notification_sent ? 'bg-blue-900 text-blue-300' : 'bg-gray-700 text-gray-500'
                            }`}>
                              {inc.notification_sent ? 'Sent' : 'Pending'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Create Rule Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-lg">
            <h3 className="text-xl font-bold text-white mb-5">New Alert Rule</h3>
            <form onSubmit={createRule} className="space-y-4">

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Rule name</label>
                <input name="name" required placeholder="e.g. High CPU on web server"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Instance <span className="text-gray-500">(leave blank to apply to all)</span>
                </label>
                <select name="resource_id"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white">
                  <option value="">All instances</option>
                  {instances.filter(i => i.status === 'running').map(i => (
                    <option key={i.id} value={i.id}>{i.name}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Metric</label>
                  <select name="metric" required
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white">
                    {Object.entries(METRIC_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>{v.split(' ')[0]}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Condition</label>
                  <select name="condition" required
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white">
                    <option value="above">Above</option>
                    <option value="below">Below</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Threshold</label>
                  <input name="threshold" type="number" required min="0" max="100" step="0.1" placeholder="80"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Duration (minutes before firing)
                </label>
                <input name="duration_minutes" type="number" defaultValue="5" min="1" max="60"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Notification email <span className="text-gray-500">(optional)</span>
                </label>
                <input name="notification_email" type="email" placeholder="alerts@example.com"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Webhook URL <span className="text-gray-500">(optional)</span>
                </label>
                <input name="notification_webhook" type="url" placeholder="https://hooks.slack.com/…"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white" />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600">
                  Cancel
                </button>
                <button type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">
                  Create Rule
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default AlertsDashboard;
